import React, { useEffect } from 'react';
import lottie from 'lottie-web';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Summary, Button } from '@sololearnorg/sol';

import { useFadeOut } from '../SlHooks/useFadeOut';

import { StreakWeek } from '../Streak/StreakWeek/StreakWeek';
import { SlTransition } from '../SlTransition/SlTransition';

import confetti from './mix-confetti-05.json';
import firstLessonConfetti from './firstLessonConfetti.json';
import counter from './counter.json';

import s from './SlStreakCelebrationScreen.scss';
import { IStreak, i18n } from '../../../symphony';

export interface ISlStreakCelebrationScreenProps {
	streak: IStreak;
	isFirstAchieve?: boolean;
	onClick: () => void;
}

interface IDescriptionXp {
	rewardAmount: number;
	currentStreakDay: number;
}

function DescriptionXp({ rewardAmount, currentStreakDay }: IDescriptionXp) {
	useStyles(s);
	return (
		<div className="streakCelebrationScreen__description">
			<p className="streakCelebrationScreen__xp-title">
				{i18n.t('web-streak.celebration-title1', { currentStreakDay })}
				<br />
				{i18n.t('web-streak.celebration-title2')}
			</p>
			<SlTransition fadeIn={{ delay: 1.3, duration: 0.3 }}>
				<div className="streakCelebrationScreen__xp">
					<p>{i18n.t('web-streak.celebration-your-reward')}</p>
					{' '}
					<p>
						+
						{rewardAmount}
						{' '}
						XP
					</p>
				</div>
			</SlTransition>
		</div>
	);
}

export const SlStreakCelebrationScreen: React.FC<ISlStreakCelebrationScreenProps> = React.memo(({ streak, onClick, isFirstAchieve }) => {
	useStyles(s);
	const { isFadeOutStarted, startFadeOut } = useFadeOut(600);

	const rewardAmount = streak.weekDays.find((w) => w.isToday)?.rewardAmount || null;

	const getFontSizeForDay = () => {
		const dayLength = streak.daysCount.toString().length;
		let fontSize = '';
		switch (dayLength) {
			case 1:
				fontSize = '64px';
				break;
			case 2:
				fontSize = '56px';
				break;
			case 3:
				fontSize = '40px';
				break;
			case 4:
				fontSize = '32px';
				break;
			default:
				fontSize = '40px';
		}
		return fontSize;
	};

	useEffect(() => {
		lottie.loadAnimation({
			animationData: counter,
			container: document.getElementById(
				'streakCelebrationScreen__circle',
			),
			autoplay: true,
			loop: false,
		});
		if (isFirstAchieve) {
			lottie.loadAnimation({
				animationData: firstLessonConfetti,
				container: document.getElementById(
					'streakCelebrationScreen__firstAchieve',
				),
				autoplay: true,
				loop: false,
			});
		} else {
			lottie.loadAnimation({
				animationData: confetti,
				container: document.getElementById(
					'streakCelebrationScreen__animation',
				),
				autoplay: true,
				loop: false,
			});
		}
	}, []);

	const setDescription = () => {
		if (rewardAmount) {
			return (
				<DescriptionXp
					rewardAmount={rewardAmount}
					currentStreakDay={streak.daysCount}
				/>
			);
		}
		if (isFirstAchieve) {
			return (
				<p className="streakCelebrationScreen__pre">
					{i18n.t('web-streak.celebration-first-achieve')}
				</p>
			);
		}
		return (
			<p className="streakCelebrationScreen__pre">
				{i18n.t('web-streak.celebration-achieve')}
			</p>
		);
	};

	return (
		<div className="streakCelebrationScreen__summary">
			<Summary
				illustration={(
					<SlTransition
						fadeOut={{ delay: 0, duration: 0.3 }}
						isFadeOutStarted={isFadeOutStarted}
					>
						<div className="streakCelebrationScreen__summary-container">
							<div
								id="streakCelebrationScreen__circle"
								className="streakCelebrationScreen__circle"
							>
								<span
									style={{
										fontSize: getFontSizeForDay(),
									}}
									className="streakCelebrationScreen__day"
								>
									{streak.daysCount}
								</span>
								{!!rewardAmount && (
									<div
										className="streakCelebrationScreen__animation"
										id="streakCelebrationScreen__animation"
									/>
								)}
								{isFirstAchieve && (
									<div
										className="streakCelebrationScreen__firstAchieve"
										id="streakCelebrationScreen__firstAchieve"
									/>
								)}
							</div>
							<SlTransition
								fadeIn={{ delay: 0.3, duration: 0.3 }}
								fadeOut={{ delay: 0.1, duration: 0.3 }}
								isFadeOutStarted={isFadeOutStarted}
							>
								<p className="streakCelebrationScreen__days">
									{i18n.t('web-streak.celebration-days', {
										count: streak.daysCount,
									})}
								</p>
							</SlTransition>
						</div>
					</SlTransition>
				)}
				headline={(
					<SlTransition
						fadeIn={{ delay: 0.6, duration: 0.3 }}
						fadeOut={{ delay: 0.1, duration: 0.3 }}
						isFadeOutStarted={isFadeOutStarted}
					>
						<div className="streakCelebrationScreen__week">
							<StreakWeek days={streak.weekDays} />
						</div>
					</SlTransition>
				)}
				description={(
					<SlTransition
						fadeIn={{ delay: 0.9, duration: 0.3 }}
						fadeOut={{ delay: 0.2, duration: 0.3 }}
						isFadeOutStarted={isFadeOutStarted}
					>
						{setDescription()}
					</SlTransition>
				)}
				actionBar
				actions={[
					<SlTransition
						fadeIn={{ delay: 0.9, duration: 0.3 }}
						fadeOut={{ delay: 0.3, duration: 0.2 }}
						isFadeOutStarted={isFadeOutStarted}
						key={1}
					>
						<Button
							key="Continue"
							onClick={() => startFadeOut(onClick)}
							sl-test-data="continueBtn"
						>
							{i18n.t('web-streak.celebration-cta')}
						</Button>
					</SlTransition>,
				]}
			/>
		</div>
	);
});
